import React from 'react';
import { Box, Container } from '@mui/material';
import type { PageProps } from '@qwickapps/react-framework';
import { Markdown, Page } from '@qwickapps/react-framework';

const termsContent = `# Terms & Conditions

**Last Updated:** February 12, 2026

## 1. Acceptance of Terms

By accessing and using QwickPDF, you accept and agree to be bound by the terms and conditions of this agreement.

## 2. Description of Service

QwickPDF is a client-side web application that allows users to convert Markdown documents to PDF format. The service operates entirely within your browser, and no data is transmitted to our servers.

## 3. Privacy and Data Processing

### 3.1 Local Processing
- All document processing occurs locally in your web browser
- Your documents and content never leave your device
- No data is uploaded to our servers

### 3.2 Local Storage
- Your documents are stored in your browser's local storage
- You can clear this data at any time using your browser settings
- We do not have access to your locally stored documents

## 4. Use License

### 4.1 Permission is granted to:
- Use QwickPDF for personal and commercial purposes
- Create and export PDF documents
- Modify typography and letterhead settings

### 4.2 You may NOT:
- Attempt to reverse engineer the application
- Use the service for any illegal purposes
- Redistribute or resell the service

## 5. Disclaimer

### 5.1 Service "As Is"
QwickPDF is provided "as is" without any representations or warranties, express or implied.

### 5.2 No Warranty
We make no warranty that:
- The service will meet your requirements
- The service will be uninterrupted, timely, secure, or error-free
- The results obtained from the use of the service will be accurate or reliable

## 6. Limitations of Liability

QwickApps shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the service.

## 7. Intellectual Property

### 7.1 Service Ownership
All rights, title, and interest in QwickPDF remain with QwickApps.

### 7.2 Your Content
You retain all rights to the documents you create using QwickPDF. We claim no ownership over your content.

## 8. Third-Party Services

QwickPDF may contain links to third-party websites or services. We are not responsible for the content or practices of these external sites.

## 9. Changes to Terms

We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to the website.

## 10. Governing Law

These terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.

## 11. Contact Information

For questions about these Terms & Conditions, please visit our contact page:

**QwickApps**
- Contact: [qwickapps.com/pages/contact](https://qwickapps.com/pages/contact)
- Website: [qwickapps.com](https://qwickapps.com)

---

**© 2026 QwickApps. All rights reserved.**
`;

class TermsPage extends Page<PageProps> {
  getPageProps(): PageProps {
    return {
      slug: 'terms',
      title: 'Terms & Conditions - QwickPDF',
      description: 'Terms and Conditions for using QwickPDF',
    };
  }

  renderView() {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            fontSize: '0.95rem',
            '& h1': { color: 'var(--theme-primary)', mt: 0, fontSize: '2rem', fontWeight: 700 },
            '& h2': { color: 'var(--theme-text)', mt: 4, mb: 2, fontSize: '1.5rem', fontWeight: 600 },
            '& h3': { color: 'var(--theme-text)', mt: 3, mb: 1.5, fontSize: '1.25rem', fontWeight: 600 },
            '& p': { mb: 2, lineHeight: 1.7, color: 'var(--theme-text-secondary)' },
            '& ul, & ol': { mb: 2, pl: 3 },
            '& li': { mb: 1, color: 'var(--theme-text-secondary)' },
            '& hr': { my: 4, borderColor: 'var(--theme-divider)' },
            '& strong': { color: 'var(--theme-text)', fontWeight: 600 },
          }}
        >
          <Markdown>{termsContent}</Markdown>
        </Box>
      </Container>
    );
  }
}

export default TermsPage;
